/**
 * Page Registry
 *
 * Allows packages to register Vue page components that can be resolved
 * dynamically by the Portal's Inertia app.
 */

import type { Component } from "vue";

export type PageModule = { default: Component & { layout?: unknown } };
export type AsyncPageModule = () => Promise<PageModule>;

const registry = new Map<string, PageModule | AsyncPageModule>();

/**
 * Register a page component.
 *
 * @param packageName Package name (e.g., 'portal-crudmanager')
 * @param pageName Page name including path (e.g., 'pages/Dashboard')
 * @param module Page module with default export or a function returning a promise
 *
 * @example
 * ```ts
 * // Sync registration
 * import { registerPage } from '@ja/portal/pageRegistry';
 * import Dashboard from './pages/Dashboard.vue';
 * registerPage('portal-crudmanager', 'pages/Dashboard', { default: Dashboard });
 *
 * // Async (lazy) registration
 * registerPage('portal-crudmanager', 'pages/Dashboard', () => import('./pages/Dashboard.vue'));
 * ```
 */
export function registerPage(
    packageName: string,
    pageName: string,
    module: PageModule | AsyncPageModule
): void {
    const key = `${packageName}::${pageName}`;
    registry.set(key, module);
}

/**
 * Get a registered page by key.
 *
 * @param key Full page key (e.g., 'portal-crudmanager::pages/Dashboard')
 */
export function getPage(key: string): PageModule | AsyncPageModule | undefined {
    return registry.get(key);
}

/**
 * Check if a page is registered.
 *
 * @param key Full page key (e.g., 'portal-crudmanager::pages/Dashboard')
 */
export function hasPage(key: string): boolean {
    return registry.has(key);
}

/**
 * Get all registered page keys.
 */
export function getRegisteredPages(): string[] {
    return Array.from(registry.keys());
}
